import React, { useState } from 'react';
import { Select, TextField, ListSubheader, MenuItem, InputAdornment, IconButton, Checkbox, FormControlLabel, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { filterRecords } from '../../utils/search';

function textOf(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(' ');
  return React.isValidElement(node) ? textOf(node.props.children) : '';
}

// Compatible with existing Select/MenuItem callers: chosen values and change
// events are still delivered by MUI, including numeric IDs and multiple values.
export default function SearchableSelect({ children, value, label, searchLabel, MenuProps = {}, onOpen, onClose, ...props }) {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const items = React.Children.toArray(children).filter(React.isValidElement);
  const emptyItems = items.filter(item => item.props.value === '');
  const groups = new Map();
  let group = '';
  const choices = items.filter(item => {
    if (item.props.value === undefined && item.props.disabled) {
      group = textOf(item.props.children);
      return false;
    }
    if (item.props.value === '') return false;
    groups.set(item, group);
    return true;
  });
  const matches = filterRecords(choices, query, item => [
    item.props['data-search-text'] || textOf(item.props.children), groups.get(item),
  ], { rank: true });
  const selected = item => Array.isArray(value) ? value.includes(item.props.value) : value === item.props.value;
  // Keep selected labels in Select's children even if hidden by the query. A
  // search must never blank the current value or produce an out-of-range value.
  const hiddenSelected = choices.filter(item => selected(item) && !matches.includes(item));
  const inputLabel = searchLabel || `Search ${typeof label === 'string' ? label.toLowerCase() : 'options'}`;

  const handleKeyDown = event => {
    if (event.key === 'Escape' || event.key === 'Tab') return;
    event.stopPropagation();
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const options = Array.from(event.currentTarget.closest('[role="listbox"]')?.querySelectorAll('[role="option"]') || [])
        .filter(option => option.hasAttribute('tabindex') && option.getAttribute('aria-disabled') !== 'true' && option.style.display !== 'none');
      (event.key === 'ArrowDown' ? options[0] : options[options.length - 1])?.focus();
    }
    if (event.key === 'Enter') event.preventDefault();
  };

  return (
    <Select
      {...props} value={value} label={label}
      inputProps={{ 'aria-label': typeof label === 'string' ? label : 'Choose option', ...props.inputProps }}
      SelectDisplayProps={{ 'aria-description': 'Open options and press / to search, or use the Search options checkbox.',
        ...props.SelectDisplayProps }}
      onOpen={event => { setQuery(''); setSearching(false); onOpen?.(event); }}
      onClose={event => { setQuery(''); setSearching(false); onClose?.(event); }}
      MenuProps={{ ...MenuProps, autoFocus: !searching, disableAutoFocusItem: searching,
        MenuListProps: { ...MenuProps.MenuListProps, onKeyDown: event => {
          if (!searching && event.key === '/') {
            event.preventDefault(); event.stopPropagation(); setSearching(true);
          }
          MenuProps.MenuListProps?.onKeyDown?.(event);
        } },
        PaperProps: { ...MenuProps.PaperProps, sx: { maxHeight: 420, ...MenuProps.PaperProps?.sx } },
      }}
    >
      <ListSubheader key="search-input" sx={{ bgcolor: 'background.paper', py: 1, lineHeight: 'normal' }}
        onClick={event => event.stopPropagation()} onKeyDown={handleKeyDown}>
        <FormControlLabel label="Search options" control={<Checkbox checked={searching}
          onKeyDown={event => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault(); event.stopPropagation(); setQuery(''); setSearching(old => !old);
            }
          }}
          onChange={event => { setQuery(''); setSearching(event.target.checked); }} />} />
        {!searching && <Typography variant="caption" component="span">Press / to search</Typography>}
        {searching && <TextField autoFocus fullWidth size="small" type="search" label={inputLabel} value={query}
          onChange={event => setQuery(event.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: query ? <InputAdornment position="end">
              <IconButton size="small" aria-label="Clear option search" onClick={() => setQuery('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment> : undefined,
          }} />}
      </ListSubheader>
      {query.trim() ? emptyItems : null}
      {query.trim() ? matches.map(item => groups.get(item) ? React.cloneElement(item, {
        children: <span>{item.props.children}<span style={{ display: 'block', fontSize: '0.75rem', opacity: 0.7 }}>{groups.get(item)}</span></span>,
      }) : item) : items}
      {matches.length === 0 && <MenuItem disabled>No matching options</MenuItem>}
      {hiddenSelected.map(item => React.cloneElement(item, { sx: { ...item.props.sx, display: 'none' } }))}
    </Select>
  );
}
